/**
 * Cross-redirect signals for the Auth0 counselor login bridge (RSW-301).
 *
 * The session bridge must ONLY exchange an Auth0 access token for our counselor
 * cookie right after the user *explicitly* started SSO (a Universal Login
 * redirect). Left ungated, the bridge would re-exchange on any mount where the
 * Auth0 SDK still holds a client session but our app session was cleared
 * (sign-out or session-expiry) — silently re-logging the counselor in and
 * bypassing sign-out / the app session timeout.
 *
 * We record an intent flag before redirecting to Auth0 and consume it exactly
 * once on return. We also broadcast app sign-out so the (provider-scoped) bridge
 * can clear the Auth0 client session, completing the logout.
 */

// Survives the full-page redirect to Auth0 and back (same tab, same origin).
export const AUTH0_LOGIN_PENDING_KEY = 'rsworks.auth0.loginPending';

// Dispatched by AuthContext on sign-out / session-expiry so the bridge can also
// clear the Auth0 SDK's client session.
export const AUTH0_APP_LOGOUT_EVENT = 'rsworks:auth0-app-logout';

export function markAuth0LoginPending() {
  try {
    window.sessionStorage.setItem(AUTH0_LOGIN_PENDING_KEY, '1');
  } catch {
    /* sessionStorage unavailable (private mode / SSR): bridge simply won't run */
  }
}

/** Returns true at most once, then clears the flag so a reload can't replay it. */
export function consumeAuth0LoginPending() {
  try {
    const pending = window.sessionStorage.getItem(AUTH0_LOGIN_PENDING_KEY) === '1';
    if (pending) {
      window.sessionStorage.removeItem(AUTH0_LOGIN_PENDING_KEY);
    }
    return pending;
  } catch {
    return false;
  }
}

export function clearAuth0LoginPending() {
  try {
    window.sessionStorage.removeItem(AUTH0_LOGIN_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/** Broadcast that the app session was cleared (sign-out or expiry). */
export function notifyAppLogout() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(AUTH0_APP_LOGOUT_EVENT));
}
